import {
    useEffect,
    useMemo,
    useState
} from "react";
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    AccordionToggleEventHandler,
    SelectTabData,
    SelectTabEvent,
    Switch,
    Tab,
    TabList,
    TabValue,
    makeStyles,
} from "@fluentui/react-components";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import {
    IApplicationRolePermission,
    IApplicationRolePermissionSection
} from "@interfaces/common.interfaces";
import {
    FieldValues,
    useFormContext,
    useWatch
} from "react-hook-form";
import {
    PermissionsSize,
    PPAPermission,
    PPAPermissions
} from "lib/permissions";
import { applicationRolePermissions } from "lib/application-role-permissions";
import { IPermissionsLevelFieldProps } from "./permissions-level-field.props";

const layoutStyles = makeStyles({
    mainWrapper: {
        display: "flex"
    }
});

export default function PermissionsLevelField<T extends FieldValues>(props: IPermissionsLevelFieldProps<T>) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { setValue, formState: { errors, isLoading } } = useFormContext<T>();
    const value = useWatch({
        name: props.fieldName
    });
    const [model, setModel] = useState<Array<boolean>>([]);
    const [selectedTab, setSelectedTab] = useState<TabValue>(applicationRolePermissions[0].title);
    const [openSections, setOpenSections] = useState([""]);

    const getAsString = (value: Array<boolean>): string => {
        return value.map((bit) => { return bit ? '1' : '0' }).join('') as string;
    }

    const getAsArray = (value: string): Array<boolean> => {
        return value.split('').map((bit: string) => { return bit === `1` });
    }

    const onTogglePermission = (permission: number) => {
        // Toggle the individual permission bool and set back on the model.
        setModel(model.map((permissionValue, modelIndex) => {
            return permission === modelIndex ? !permissionValue : permissionValue;
        }));
    }

    useEffect(() => {
        // Do not set the model if the form is still loading.
        if (isLoading)
            return;

        // Ensure the form value is a string at least the length of the number of current permissions.
        const permissionLevelString = (value as string || "").padEnd(PermissionsSize, `0`);
        // Only set the model if the form value is different to the model.
        if (permissionLevelString !== getAsString(model))
            // Create the model as an array of bool for each bit in the permission level string.
            setModel(getAsArray(permissionLevelString));
    }, [value]);

    useEffect(() => {
        // Do not set the value back on the form if the form is still loading.
        if (isLoading)
            return;

        // Do not set the value back on the form if the model has not been initialised.
        if (model.length === 0)
            return;

        // Get the model as a string of bits.
        const modelString = getAsString(model);
        // Only set the form value if it is different to the model.
        if (modelString !== value)
            setValue(props.fieldName, modelString as any);
    }, [model]);

    const handleToggleSection: AccordionToggleEventHandler<string> = (event, data) => {
        setOpenSections(data.openItems);
    };

    const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
        setSelectedTab(data.value);
        setOpenSections([""]);
    };

    const getPermissionName = (permission: PPAPermission) => t(`ppaPermission${Object.keys(PPAPermissions)[Object.values(PPAPermissions).indexOf(permission)]}` as any);

    const getTotalPermissionCount = (section: IApplicationRolePermissionSection): number => {
        return section.permissions.length;
    }

    const getSelectedPermissionCount = (section: IApplicationRolePermissionSection): number => {
        var count = 0;
        section.permissions.forEach(p => {
            count += model[p] ? 1 : 0;
        });
        return count;
    }

    const getTabCount = (tab: IApplicationRolePermission): string => {
        var selectedCount = 0;
        var permissionCount = 0;
        tab.sections.forEach(s => {
            permissionCount += getTotalPermissionCount(s);
            selectedCount += getSelectedPermissionCount(s);
        });
        return t('generalAOfBLabel', { a: selectedCount, b: permissionCount });
    }

    const getSectionCount = (section: IApplicationRolePermissionSection): string => {
        const selectedCount = getSelectedPermissionCount(section);;
        const permissionCount = getTotalPermissionCount(section);
        return t('generalAOfBLabel', { a: selectedCount, b: permissionCount });
    }

    return (
        <div className={styles.mainWrapper}>
            <TabList selectedValue={selectedTab} onTabSelect={onTabSelect} vertical>
                {applicationRolePermissions.map((tab) => (
                    <Tab key={tab.title} value={tab.title}>{t(tab.title)} ({getTabCount(tab)})</Tab>
                ))}
            </TabList>
            <Accordion
                openItems={openSections}
                onToggle={handleToggleSection}
                collapsible>
                {applicationRolePermissions.find(t => t.title === selectedTab)?.sections?.map((item) => (
                    <AccordionItem key={item.title} value={item.title}>
                        <AccordionHeader>{t(item.title)} ({getSectionCount(item)})</AccordionHeader>
                        <AccordionPanel>
                            {item.permissions.map((item2) => (
                                <div key={item2}>
                                    <Switch
                                        label={getPermissionName(item2)}
                                        checked={model[item2]}
                                        onChange={() => { onTogglePermission(item2) }} />
                                </div>
                            ))}
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    );
}